import React from 'react';
import {Button, Tooltip} from 'antd';

import styles from './taskCreditDialogs.module.less';
import StarSvg from '@/assets/icons/quasStar.svg';
import {formatNumberWithCommas} from '@/utils';

interface IModalApi {
    confirm: (config: any) => {
        destroy: () => void;
        update: (configUpdate: any) => void;
    };
}

interface IInsufficientCreditsDialogParams {
    modal: IModalApi;
    estimatedCost: number;
    availableCredits: number;
    gap: number;
    userLevel?: string;
    onRecharge?: () => void;
}

interface IStartTaskConfirmDialogParams {
    modal: IModalApi;
    taskName: string;
    estimatedCost: number;
    availableCredits: number;
    showRechargeSuccessTip?: boolean;
    onConfirm: () => Promise<void> | void;
}

interface IReEvolveConfirmDialogParams {
    modal: IModalApi;
    requiredCredits: number;
    onConfirm: () => Promise<void> | void;
    /** 点击"取消"按钮或关闭弹窗时触发的回调（先于 modal.destroy 执行） */
    onCancel?: () => void;
    /** 任务数是否已达上限；true 时禁用确认按钮并显示 tip */
    taskLimitExceeded?: boolean;
    /** 用户当前可创建的最大任务数 */
    maxTaskCount?: number;
    /** 是否为进阶版用户 */
    isPro?: boolean;
    /** 是否为基础版/免费版用户 */
    isFree?: boolean;
    /** 点击 tip 中"升级订阅"链接的回调（一般用于跳转 /billing） */
    onUpgrade?: () => void;
    /** 点击 tip 中"联系商务"链接的回调（一般用于打开企业联系表单弹窗） */
    onContact?: () => void;
    /** 确认按钮 loading 状态，防止重复点击 */
    loading?: boolean;
}

interface ITaskLimitExceededDialogParams {
    modal: IModalApi;
    currentTaskCount: number;
    maxTaskCount: number;
    isPro: boolean;
    isFree: boolean;
    onUpgrade?: () => void;
}

export function showInsufficientCreditsModal(params: IInsufficientCreditsDialogParams) {
    const {modal, estimatedCost, availableCredits, gap, onRecharge} = params;

    modal.confirm({
        icon: null,
        title: '资源点不足，无法启动任务',
        okText: '前往充值',
        cancelText: '取消',
        centered: true,
        content: (
            <>
                <div className={styles.creditsCard}>
                    <div className={`${styles.creditsRow} ${styles.creditsRowBordered}`}>
                        <span className={styles.creditsLabel}>本次任务需要</span>
                        <span className={styles.creditsValueCost}>{formatNumberWithCommas(estimatedCost)} 资源点</span>
                    </div>
                    <div className={`${styles.creditsRow} ${styles.creditsRowBordered}`}>
                        <span className={styles.creditsLabel}>当前可用资源点</span>
                        <span className={styles.creditsValueAvailable}>{formatNumberWithCommas(availableCredits)} 资源点</span>
                    </div>
                    <div className={styles.creditsRow}>
                        <span className={styles.creditsLabel}>差额</span>
                        <span className={styles.creditsValueCost}>{formatNumberWithCommas(gap)} 资源点</span>
                    </div>
                    
                </div>
                <div className={styles.footerNoteLine}>请前往充值系统充值或升级账号后继续。</div>
           </>
        ),
        onOk: () => onRecharge?.()
    });
}

export function showStartTaskConfirmModal(params: IStartTaskConfirmDialogParams) {
    const {
        modal,
        taskName,
        estimatedCost,
        availableCredits,
        showRechargeSuccessTip,
        onConfirm
    } = params;

    modal.confirm({
        icon: null,
        title: '启动任务？',
        okText: '确认启动',
        cancelText: '取消',
        centered: true,
        content: (
            <div>
                {showRechargeSuccessTip && (
                    <div className={styles.rechargeSuccessTip}>
                        ✓ 充值成功，当前可用资源点已更新
                    </div>
                )}
                <div className={styles.taskDesc}>
                    任务<span className={styles.taskNameHighlight}>「{taskName}」</span>启动后会先进行初始化校验。
                </div>
                <div className={styles.creditsCard}>
                    <div className={`${styles.creditsRow} ${styles.creditsRowBordered}`}>
                        <span className={styles.creditsLabel}>预计消耗</span>
                        <span className={styles.creditsValueCost}>{formatNumberWithCommas(estimatedCost)} 资源点</span>
                    </div>
                    <div className={styles.creditsRow}>
                        <span className={styles.creditsLabel}>当前可用</span>
                        <span className={styles.creditsValueAvailable}>{formatNumberWithCommas(availableCredits)} 资源点</span>
                    </div>
                </div>
                <div className={styles.footerNote}>
                    <div>初始化校验通过后，任务将自动进入演化阶段。</div>
                    <div>如校验或执行失败，未消耗资源点会自动返还。</div>
                </div>
            </div>
        ),
        onOk: () => {
            void onConfirm();
        }
    });
}

export function showReEvolveConfirmModal(params: IReEvolveConfirmDialogParams) {
    const {modal, requiredCredits, onConfirm, onCancel, taskLimitExceeded, maxTaskCount, isPro, isFree, onUpgrade, onContact, loading} = params;

    // 用闭包暂存 modal.confirm 的返回值，关闭时调用它的 destroy()
    let modalInstance: {destroy: () => void; update: (configUpdate: any) => void} | null = null;
    let confirmLoading = !!loading;
    const handleClose = () => {
        onCancel?.();
        modalInstance?.destroy?.();
    };

    // 任务数已达上限时，根据用户类型展示不同 tip
    const isEnterprise = !isPro && !isFree;
    const limitTipNode = taskLimitExceeded ? (
        <span className={styles.limitTipWrapper}>
            任务数量已达上限（{maxTaskCount ?? 0}），如需提升请&nbsp;
            {(isEnterprise || isPro) ? (
                <span
                    className={styles.limitTip}
                    onClick={() => {
                        modalInstance?.destroy?.();
                        onContact?.();
                    }}
                >
                    联系商务
                </span>
            ) : (
                <span
                    className={styles.limitTip}
                    onClick={() => {
                        modalInstance?.destroy?.();
                        onUpgrade?.();
                    }}
                >
                    升级订阅
                </span>
            )}
        </span>
    ) : undefined;

    const renderOkButton = () => (
        <Button
            type="primary"
            loading={confirmLoading}
            onClick={async () => {
                const updateFooter = () => {
                    modalInstance?.update?.({
                        footer: renderFooter()
                    });
                };

                confirmLoading = true;
                updateFooter();
                try {
                    // 自定义 footer 时 antd 不会自动关闭弹窗，这里在 onConfirm 完成后手动销毁
                    await onConfirm();
                    modalInstance?.destroy?.();
                }
                catch (error) {
                    confirmLoading = false;
                    updateFooter();
                    throw error;
                }
            }}
            disabled={!!taskLimitExceeded}
        >
            确认重新演化
        </Button>
    );

    const renderFooter = () => {
        const okButton = renderOkButton();

        return (
            <div className={styles.creditsTaskCardFooter}>
                <Button onClick={handleClose}>取消</Button>
                {limitTipNode ? (
                    <Tooltip
                        title={limitTipNode}
                        placement="top"
                        classNames={{root: styles.limitTipTooltip}}
                    >
                        <span>{okButton}</span>
                    </Tooltip>
                ) : (
                    okButton
                )}
            </div>
        );
    };

    modalInstance = modal.confirm({
        icon: null,
        title: '确认重新演化此任务？',
        okText: '确认重新演化',
        cancelText: '取消',
        centered: true,
        content: (
            <div className={styles.creditsTaskCard}>
                <div className={styles.creditsTaskContext}>
                    <span className={styles.creditsLabel}>本次预计消耗</span>
                    <div className={styles.creditsValueWrapper}>
                        <StarSvg className={styles.starIcon} />
                        <span className={styles.creditsValue}>
                            {formatNumberWithCommas(requiredCredits)}
                        </span>
                    </div>
                </div>
                 <div className={styles.confirmDesc}>
                    确认后，将跳转到新的任务，原演化任务保留不变。
                </div>
            </div>
        ),
        footer: renderFooter(),
        onOk: () => onConfirm(),
        onCancel: handleClose
    });
}

interface IEnableConfigDialogContentParams {
    /** 是否资源点不足 */
    insufficientCredits: boolean;
    /** 是否有轮次变化 */
    hasIterationChange: boolean;
    /** 原始最大演化轮次 */
    prevMaxIterations?: number;
    /** 当前最大演化轮次 */
    currentMaxIterations?: number;
    /** 轮次差 */
    iterationDiff: number;
    /** 需额外资源点 */
    estimatedCost: number;
    /** 当前可用资源点 */
    availableCredits: number;
    /** 差额 */
    gap: number;
    /** 缺口资源点（后端返回） */
    shortfallCredits?: number;
    /** 轮次差额资源点 */
    accumulationCredits?: number;
}

/**
 * 获取启用新配置弹窗的内容
 * 用于 ColdStart 组件中的 enableModalContent
 */
export function getEnableConfigDialogContent(params: IEnableConfigDialogContentParams) {
    const {
        insufficientCredits,
        iterationDiff,
        hasIterationChange,
        prevMaxIterations,
        currentMaxIterations,
        estimatedCost,
        availableCredits,
        accumulationCredits,
        gap
    } = params;

    /** 资源点不足：优先显示资源点不足内容（无论是否有轮次变化） */
    if (insufficientCredits) {
        /** 有轮次变化：显示完整的资源点不足信息 */
        if (hasIterationChange) {
            return (
                <>
                    <div className={styles.creditsCard}>
                        <div className={`${styles.creditsRow} ${styles.creditsRowBordered}`}>
                            <span className={styles.creditsLabel}>轮次调整</span>
                            <span className={styles.creditsValueAvailable}>{prevMaxIterations} → {currentMaxIterations} 轮</span>
                        </div>
                        <div className={`${styles.creditsRow} ${styles.creditsRowBordered}`}>
                            <span className={styles.creditsLabel}>需额外资源点</span>
                            <span className={styles.creditsValueCost}>{formatNumberWithCommas(accumulationCredits ?? estimatedCost)} 资源点</span>
                        </div>
                        <div className={`${styles.creditsRow} ${styles.creditsRowBordered}`}>
                            <span className={styles.creditsLabel}>当前可用资源点</span>
                            <span className={styles.creditsValueAvailable}>{formatNumberWithCommas(availableCredits)} 资源点</span>
                        </div>
                        <div className={styles.creditsRow}>
                            <span className={styles.creditsLabel}>差额</span>
                            <span className={styles.creditsValueCost}>{formatNumberWithCommas(gap)} 资源点</span>
                        </div>
                    </div>
                    <div className={styles.footerNoteLine}>请前往充值系统充值后再调整配置。</div>
                </>
            );
        }

        /** 无轮次变化：显示简化的资源点不足信息 */
        return (
            <>
                <div className={styles.creditsCard}>
                    <div className={`${styles.creditsRow} ${styles.creditsRowBordered}`}>
                        <span className={styles.creditsLabel}>需额外资源点</span>
                        <span className={styles.creditsValueCost}>{formatNumberWithCommas(accumulationCredits ?? estimatedCost)} 资源点</span>
                    </div>
                    <div className={`${styles.creditsRow} ${styles.creditsRowBordered}`}>
                        <span className={styles.creditsLabel}>当前可用资源点</span>
                        <span className={styles.creditsValueAvailable}>{formatNumberWithCommas(availableCredits)} 资源点</span>
                    </div>
                    {/* <div className={styles.creditsRow}>
                        <span className={styles.creditsLabel}>差额</span>
                        <span className={styles.creditsValueCost}>{gap} 资源点</span>
                    </div> */}
                </div>
                <div className={styles.footerNoteLine}>请前往充值系统充值后再启用配置。</div>
            </>
        );
    }
    /** 有轮次变化且资源点充足：显示正常确认内容 */
    return (
        <>
            <div className={styles.confirmDesc}>
                启用后，新的配置将立即生效并覆盖当前的设置。
            </div>
            <div className={styles.creditsCard}>
                {iterationDiff > 0 && (
                    <>
                        <div className={`${styles.creditsRow} ${styles.creditsRowBordered}`}>
                            <span className={styles.creditsLabel}>轮次调整</span>
                            <span className={styles.creditsValueAvailable}>{prevMaxIterations} → {currentMaxIterations} 轮</span>
                        </div>
                        <div className={`${styles.creditsRow} ${styles.creditsRowBordered}`}>
                            <span className={styles.creditsLabel}>需额外资源点</span>
                            <span className={styles.creditsValueCost}>{formatNumberWithCommas(accumulationCredits ?? estimatedCost)} 资源点</span>
                        </div>
                    </>
                )}
                <div className={styles.creditsRow}>
                    <span className={styles.creditsLabel}>当前可用资源点</span>
                    <span className={styles.creditsValueAvailable}>{formatNumberWithCommas(availableCredits)} 资源点</span>
                </div>
            </div>
            <div className={styles.footerNoteLine}>此操作无法撤销，请谨慎操作。</div>
        </>
    );
}

/**
 * 获取启用新配置弹窗的标题
 */
export function getEnableConfigModalTitle(insufficientCredits: boolean, hasIterationChange: boolean): string {
    if (insufficientCredits) {
        return hasIterationChange ? '资源点不足，无法增加演化轮次' : '资源点不足，无法启用配置';
    }
    return '确定启用新的任务配置？';
}

/**
 * 任务数量超限弹窗
 */
export function showTaskLimitExceededModal(params: ITaskLimitExceededDialogParams) {
    const {modal, currentTaskCount, maxTaskCount, isPro, isFree, onUpgrade} = params;
    const planText = isFree ? '基础版账号' : (isPro ? '进阶版账号' : '企业账号');
    const upgradePlan = isFree ? '基础版' : (isPro ? '进阶版' : '企业版');

    modal.confirm({
        icon: null,
        title: '任务数达上限',
        okText: '立即升级',
        cancelText: '取消',
        centered: true,
        content: (
            <div>
                <div className={styles.confirmDesc}>
                    您的{planText}仅支持创建 {maxTaskCount} 个任务，已达上限，无法创建新任务。
                    如需提升配额，请升级{upgradePlan}账号。
                </div>
                <div className={styles.creditsCard}>
                    <div className={`${styles.creditsRow} ${styles.creditsTaskRow}`}>
                        <span className={styles.creditsLabel}>当前任务数</span>
                        <span className={styles.creditsValueCost}>{currentTaskCount} / {maxTaskCount}</span>
                    </div>
                </div>
            </div>
        ),
        onOk: () => onUpgrade?.()
    });
}
